import { Injectable } from '@angular/core';

import { CommsService, FoldersDataService, ISelectMenuItem } from '@services';
import { IObjectStringKeyMap } from '@shared/models';
import { Events } from '@services/events/events.service';

import { NGXLogger } from 'ngx-logger';
import { cloneDeep, each, isNumber, min} from 'lodash';
import { TranslateService } from '@ngx-translate/core';

export enum ExternalEventItemClass {
  assetTracker = 'assetTracker',
  workerTracker = 'workerTracker',
  tempSensor = 'tempSensor',
}

export enum ExternalEventItemMethod {
  push = 'push',
  pull = 'pull',
  pullOnChange = 'pullOnChange'
}

export enum ExternalEventItemWhen {
  always = 'always',
  onChange = 'onChange',
  periodic = 'periodic'
}

export enum ExternalEventSourceType {
  erdmanPLC = 'erdmanPLC',
  minewIoT = 'minewIoT',
  advantechWISE = 'advantechWISE'
}

export interface IExternalEventItem {
  active: number;
  published: number;
  itemID: number;
  alternateTags: string[] | string;
  addedAt: number;
  addedBy: number;
  disabledAt: number;
  disabledBy: number;
  lastUpdate: number;
  sourceType: ExternalEventSourceType;
  folderID: number;
  uniqueName: string;
  label: string;
  attributes: {
    method: ExternalEventItemMethod ;
    relatedItem?: number;
    frequency?: number;
    interval?: number;
    updateWhen?: ExternalEventItemWhen;
    changeDelta?: number;
    scope?: string;
    tag?: string;
    allowAlternateTags?: number;
    alternateTags?: string[] | string;
    /** capture condition evaluated by the PLC scraper, e.g. `{{this}} > 0` (CVX-6430); '' = none */
    condition?: string;
    /** Advantech WISE digital input the item reads: di1…di4 */
    channel?: string;
    /** in-use debounce on WISE and PLC items, whole seconds >= 0; 0 or absent = none (CVX-6507, CVX-6536) */
    padding?: number;
    /** PLC item feeding an In Use property: an expression, or "increased"; absent = raw value (CVX-6536) */
    inUseWhen?: string;
  }
}
export interface IAttachedEventItem {
  folderID?: number;
  itemID?: number;
  tag?: string;
}

export interface IExternalEventSource {
  active: number;
  addedAt: number;
  addedBy: number;
  disabledAt: number;
  disabledBy: number;
  lastUpdate: number;
  sourceID: number;
  sourceType: ExternalEventSourceType ;
  locationID: number;
  gatewayID: number;
  folderID: number;
  name: string;
  attributes: IExternalEventSourceAttributes;
}

export interface IExternalEventSourceAttributes {
  zoneID?: number;
  address?: string;
  parentEventItems?: IAttachedEventItem[];
  selectedEventItems?: IAttachedEventItem[];
  eventItems?: IAttachedEventItem[]
  signature?: string;
  /** Advantech WISE: the unit's ID in its default topic, Advantech/{deviceID}/data */
  deviceID?: string;
  /** Advantech WISE: optional topic that replaces the default one */
  topic?: string;
}

@Injectable({
  providedIn: 'root'
})
export class ExternalEventsService {
  private eventSources = {
    lastRequest: null,
    data: {} as IObjectStringKeyMap<IExternalEventSource>
  };

  private eventItems = {
    lastRequest: null,
    data: {} as IObjectStringKeyMap<IExternalEventItem>
  };

  constructor(
    private commsService: CommsService,
    private events: Events,
    private logger: NGXLogger,
    private translate: TranslateService,
    private folderDataService: FoldersDataService
  ) {}

  public refresh(updated?: number): Promise<IObjectStringKeyMap<IExternalEventSource>> {
    const last = min([this.eventSources?.lastRequest, this.eventItems?.lastRequest ]);
    if (updated && updated < last) {
      this.logger.log(`local eventSources cache already up to date: ${updated}, ${last}`);
      return Promise.resolve(this.eventSources.data);
    } else {
      const requestData: any = {
        cmd: 'getExternalSources',
        includeDisabled: 1,
        includeItems: 1,
        lastRequest: this.eventSources.lastRequest,
      };

      return this.commsService.sendMessage(requestData, false, false).then((response) => {
        if (response?.reqStatus === 'OK') {
          this.updateCache(response);
        }

        return this.eventSources.data;
      });
    }
  }

  public updateCache(data) {
    if (data.result?.sources) {
      this.eventSources.data = data.result.sources;
      this.eventSources.lastRequest = data.result.timestamp;
      this.events.publish('ccs:eventSourcesUpdate', true);
    }
    if (data.result?.items) {
      this.eventItems.data = data.result.items;
      this.eventItems.lastRequest = data.result.timestamp;
      this.events.publish('ccs:eventItemsUpdate', true);
    }
  }

  public getLocalItems() {
    return cloneDeep(this.eventItems.data);
  }

  public hasLoaded(): boolean {
    return this.eventSources.lastRequest !== null;
  }

  public getLocalSources() {
    return cloneDeep(this.eventSources.data);
  }

  public getSourcesAsList(includeInactive: boolean = false, includeDisabled: boolean = false): IExternalEventSource[] {
    const theList: IExternalEventSource[] = [];

    each(this.eventSources.data, (item) => {
      if (!includeInactive && !item.active) {
        return;
      }
      if (!includeDisabled && item.disabledAt) {
        return;
      }
      theList.push(item);
    });

    return theList;
  }

  public getSources(): IObjectStringKeyMap<IExternalEventSource> {
    return this.eventSources.data;
  }

  public getSourceById(id: number): IExternalEventSource {
    return cloneDeep(this.eventSources.data[id]);
  }

  public displaySourceName(source: IExternalEventSource | number): string {
    if (isNumber(source) ) {
      source  = this.getSourceById(source);
    }
    if (!source) {
      return this.translate.instant('SHARED.Unknown');
    } else {
      return source.name || this.translate.instant('SHARED.None');
    }
  }

  public displayItemName(item: IExternalEventItem | number, includeFolders: boolean = false): string {
    if ( isNumber(item) ) {
      item  = this.getItemById(item);
    }
    if (!item) {
      return this.translate.instant('SHARED.Unknown');
    }
    let ret = this.getItemDescription(item, false);
    if (includeFolders && item.folderID) {
      ret = `${this.folderDataService.getFolderPath(item.folderID, false, '/ ', true)}/ ${ret}`;
    }
    return ret;
  }

  public addSource(data: any): Promise<any> {
    const requestData = {
      cmd: 'addExternalSource',
      ...data
    };
    return this._updateAndRefresh(requestData, () => this.refresh());
  }

  public updateSource(data: any): Promise<any> {
    const requestData = {
      cmd: 'updateExternalSource',
      ...data
    };
    return this._updateAndRefresh(requestData, () => this.refresh());
  }

  public deleteSource(id: number): Promise<any> {
    const requestData = {
      cmd: 'deleteExternalSource',
      sourceID: id
    };

    return this._updateAndRefresh(requestData, () => this.refresh());
  }

  public getItemDescription(item: number | IExternalEventItem, includePrefix: boolean = true): string {
    if ( isNumber(item) ) {
      item  = this.getItemById(item);
    }
    let ret = this.translate.instant('SHARED.Unknown');
    if (item) {
      ret = includePrefix ? `${this.getSourceTypeName(item.sourceType)}: ` : '';
      if (item.label !== '') {
        ret += `${item.label} (${item.uniqueName})`;
      } else {
        ret += item.uniqueName;
      }
    }
    return ret;
  }

  public getItemsAsList(filter?, includeInactive: boolean = false, includeDisabled: boolean = false): IExternalEventItem[] {
    const theList: IExternalEventItem[] = [];

    each(this.eventItems.data, (item) => {
      if (!includeInactive && !item.active) {
        return;
      }
      if (!includeDisabled && item.disabledAt) {
        return;
      }
      if (filter) {
        if (filter?.type && item.sourceType !== filter.type) {
          return;
        }
        if (filter?.omit && item.itemID === filter.omit) {
          return;
        }
      }
      theList.push(item);
    });

    return theList;
  }

  public getItems(): IObjectStringKeyMap<IExternalEventItem> {
    return this.eventItems.data;
  }

  public getItemById(id: number): IExternalEventItem {
    return cloneDeep(this.eventItems.data[id]);
  }

  public addItem(data: any): Promise<any> {
    const requestData = {
      cmd: 'addExternalItem',
      ...data
    };
    return this._updateAndRefresh(requestData, () => this.refresh());
  }

  public updateItem(data: any): Promise<any> {
    const requestData = {
      cmd: 'updateExternalItem',
      ...data
    };
    return this._updateAndRefresh(requestData, () => this.refresh());
  }

  public deleteItem(id: number): Promise<any> {
    const requestData = {
      cmd: 'deleteExternalItem',
      itemID: id
    };

    return this._updateAndRefresh(requestData, () => this.refresh());
  }

  public getSourceTypeList(): ISelectMenuItem[] {
    const types = [
      {
        id: ExternalEventSourceType.erdmanPLC,
        description: 'Erdman'
      },
      {
        id: ExternalEventSourceType.minewIoT,
        description: 'Minew'
      },
      {
        id: ExternalEventSourceType.advantechWISE,
        description: 'Advantech WISE'
      }
    ];
    return types;
  }

  public getSourceTypeName(type: ExternalEventSourceType): string {
    let ret = 'SHARED.None';

    switch (type) {
      case ExternalEventSourceType.erdmanPLC: {
        ret = 'Erdman'
        break;
      }
      case ExternalEventSourceType.minewIoT: {
        ret = 'Minew'
        break;
      }
      case ExternalEventSourceType.advantechWISE: {
        ret = 'Advantech WISE'
        break;
      }
    }
    return ret;
  }

  public getItemMethodName(type: ExternalEventItemMethod): string {
    let ret = 'SHARED.Unknown';

    switch (type) {
      case ExternalEventItemMethod.push: {
        ret = this.translate.instant('EXTERNAL.Push');
        break;
      }
      case ExternalEventItemMethod.pull: {
        ret = this.translate.instant('EXTERNAL.Pull');
        break;
      }
      case ExternalEventItemMethod.pullOnChange: {
        ret = this.translate.instant('EXTERNAL.PullOnChange');
        break;
      }
    }
    return ret;
  }

  public getItemWhenName(type: ExternalEventItemWhen): string {
    let ret = 'SHARED.Unknown';

    switch (type) {
      case ExternalEventItemWhen.always: {
        ret = this.translate.instant('EXTERNAL.Always');
        break;
      }
      case ExternalEventItemWhen.onChange: {
        ret = this.translate.instant('EXTERNAL.onChange');
        break;
      }
      case ExternalEventItemWhen.periodic: {
        ret = this.translate.instant('EXTERNAL.Periodic');
        break;
      }
    }
    return ret;
  }

    private _updateAndRefresh(fData, updateHandler): Promise<any> {
    const cmdOpts = cloneDeep(fData);

    return new Promise((resolve, reject) => {
      this.commsService.sendMessage(cmdOpts, false, false)
        .then((data) => {
          if (data && data.reqStatus === 'OK') {
            updateHandler()
              .then((ores) => {
                resolve(data);
              })
              .catch((oerr) => {
                this.logger.error('update failed: ' + oerr);
              });
          } else {
            resolve(data);
          }
        })
        .catch((err) => {
          this.logger.error('update failed: ' + err);
          reject(err);
        });
    });
  }

}
